<template>
  <div>
    <v-skeleton-loader
      v-if="isLoading"
      type="article, actions"
    />

    <empty-state
      v-else-if="displayErrorMessage"
      :image="emptyStateImage"
      :title="$t('ingo.sessionError.title')"
      :message="$t('ingo.sessionError.message')"
    />

    <transition name="fade">
      <div
        v-show="isFormLoaded"
        id="ingo-pay-container"
        ref="container"
      />
    </transition>
  </div>
</template>

<script>
import Vue from 'vue'
import { mapActions, mapGetters } from 'vuex'
import {
  INGO_PAY_CLIENT_SCRIPT_ID,
  INGO_PAY_CLIENT_SCRIPT_URL
} from '@/components/direct-payments/direct-payment-constants'
import * as actionTypes from '@/store/action-types'
import * as getTypes from '@/store/get-types'
import { CATALOG_ROUTE } from '@/router/routes/route-names'
import { DirectPaymentApi } from '@/cowboy/payment/DirectPaymentApi'
import EmptyState from '@/components/shared/EmptyState'
import emptyStateImage from '@/assets/images/empty-state-unplugged.svg'

export default {
  name: 'IngoPayContainer',
  components: { EmptyState },
  data: () => ({
    ingoInstantPayments: null,
    isFormLoadSuccessful: null,
    webPlugin: null
  }),
  computed: {
    ...mapGetters({
      paymentType: getTypes.GET_DIRECT_PAYMENT_TYPE
    }),
    displayErrorMessage () {
      return this.isFormLoadSuccessful === false
    },
    emptyStateImage () {
      return emptyStateImage
    },
    ingoFundingDestination () {
      const { FUNDING_DESTINATIONS } = this.ingoInstantPayments || {}
      const { [this.paymentType]: fundingDestination } =
        FUNDING_DESTINATIONS || {}
      return fundingDestination
    },
    isFormLoaded () {
      return this.isFormLoadSuccessful === true
    },
    isLoading () {
      return this.isFormLoadSuccessful === null
    }
  },
  async mounted () {
    await this.loadScriptIfNotExists()
    this.createWebPlugin()
    await this.loadWebPluginContent()
  },
  methods: {
    ...mapActions({
      setSessionId: actionTypes.SET_DIRECT_PAYMENT_SESSION_ID
    }),
    createScriptElement () {
      const ingoPayScript = document.createElement('script')
      ingoPayScript.type = 'text/javascript'
      ingoPayScript.async = true
      ingoPayScript.src = INGO_PAY_CLIENT_SCRIPT_URL
      ingoPayScript.id = INGO_PAY_CLIENT_SCRIPT_ID
      return ingoPayScript
    },
    createWebPlugin () {
      if (this.ingoInstantPayments && !this.webPlugin) {
        const webPlugin = this.ingoInstantPayments.create(
          this.$refs.container,
          {
            cssName: 'ingo-web-plugin',
            autoHeight: true,
            scrolling: false
          }
        )

        Vue.set(this, 'webPlugin', webPlugin)

        this.webPlugin.addEventListener(
          this.ingoInstantPayments.EVENTS.PAGE_LOAD,
          this.handleIframePageLoadEvent
        )

        this.webPlugin.addEventListener(
          this.ingoInstantPayments.EVENTS.TOKEN_SUCCESS,
          this.handleIframeTokenSuccessEvent
        )

        this.webPlugin.addEventListener(
          this.ingoInstantPayments.EVENTS.FUNDING_CANCELED,
          this.returnToCatalog
        )

        this.webPlugin.addEventListener(
          this.ingoInstantPayments.EVENTS.TERMINAL_FAILURE_ACKNOWLEDGED,
          this.returnToCatalog
        )
      }
    },
    async loadScriptIfNotExists () {
      await new Promise((resolve, reject) => {
        const existingIngoPayScript = document.querySelector(
          `#${INGO_PAY_CLIENT_SCRIPT_ID}`
        )
        if (existingIngoPayScript) {
          resolve()
        } else {
          const ingoPayScript = this.createScriptElement()
          ingoPayScript.addEventListener('load', resolve)
          ingoPayScript.addEventListener('error', reject)
          ingoPayScript.addEventListener('abort', reject)
          document.head.appendChild(ingoPayScript)
        }
      })

      Vue.set(this, 'ingoInstantPayments', window.IngoInstantPayments)
    },
    async loadWebPluginContent () {
      if (this.webPlugin && this.ingoFundingDestination) {
        try {
          const createSessionResponse = (await DirectPaymentApi.createIngoSession()).toJSON()
          await this.webPlugin.mount(createSessionResponse.authorizedUrl, this.ingoFundingDestination)
          await this.setSessionId(createSessionResponse.sessionIdentifier)
        } catch (error) {
          this.isFormLoadSuccessful = false
        }
      }
    },
    handleIframePageLoadEvent (event) {
      const { page } = event?.content || {}
      const ingoPages = this.ingoInstantPayments.PLUGIN_PAGES
      if (
        page === ingoPages.ACH_FUNDING ||
        page === ingoPages.DEBIT_FUNDING
      ) {
        this.isFormLoadSuccessful = true
      } else if (
        page === ingoPages.ACH_FUNDING_CONFIRM ||
        page === ingoPages.DEBIT_FUNDING_CONFIRM
      ) {
        this.$emit('is-reviewing-payment', true)
      }
    },
    handleIframeTokenSuccessEvent (event) {
      // Only do this only for environments where the Ingo webhook is not configured.
      if (event && process.env.VUE_APP_DISPLAY_INGO_TOKEN_DATA) {
        prompt(
          'localhost - \n' +
            'Copy this sessionIdentifier and customerAccountToken to ' +
            'your payment-supplier webhook token success event body, ' +
            'then make the rest call BEFORE clicking `OK` to this prompt.',
          '"sessionIdentifier": "' +
            event.session_identifier +
            '",\n"customerAccountToken": "' +
            event.customer_account_token +
            '"\n'
        )
      }

      this.$emit('submit-payment')
    },
    returnToCatalog () {
      this.$router.push({ name: CATALOG_ROUTE })
    }
  }
}
</script>

<style lang="scss" scoped>
// The ingo iframe has a padding on the body
$ingo-iframe-padding: 15px;

#ingo-pay-container {
  border: none;
  flex: 1 1 auto;
  // Offset the horizontal padding from the body to avoid having to add that same padding to all
  // of our other content
  margin: 0px (0px -$ingo-iframe-padding)
}

::v-deep {
  iframe.ingo-web-plugin {
    border: none;
    width: 100%;
    height: 100%;
  }
}

#checkout-update-panel {
  width: 100%;
  .card-logo {
    .img-responsive {
      width: 100%;
    }
  }
}
</style>
